import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    Footprints,
    Mountain,
    Bike,
    ShoppingBag,
    Link as LinkIcon,
    Tag,
    LayoutGrid,
} from "lucide-react";
import "./GroupRegistration.scss";
import { fetchGroupRegistrations, trackButtonClick } from "../../services/googleSheetsService";

// 莫蘭迪色系配色表
const getMorandiColor = (type) => {
    if (!type) return { bg: "#E0E0E0", text: "#555555" }; // Default grey

    // 移除空白並轉為小寫以確保一致性
    const normalizedType = type.trim().toLowerCase();
    if (!normalizedType) return { bg: "#E0E0E0", text: "#555555" };

    // 特定關鍵字指定顏色 (避免顏色相近)
    const fixedColors = {
        路跑: { bg: "#9CAFB7", text: "#FFFFFF" }, // 藍灰
        越野跑: { bg: "#C4A484", text: "#FFFFFF" }, // 卡其 (大地色)
        三鐵: { bg: "#D4A5A5", text: "#FFFFFF" }, // 粉紅
        喵喵選物: { bg: "#e2bd73ff", text: "#FFFFFF" }, // 粉紅
        其他連結: { bg: "#66b990ff", text: "#FFFFFF" }, // 粉紅
    };

    if (fixedColors[normalizedType]) {
        return fixedColors[normalizedType];
    }

    const colors = [
        { bg: "#9CAFB7", text: "#FFFFFF" }, // 莫蘭迪藍灰
        { bg: "#B5A397", text: "#FFFFFF" }, // 莫蘭迪褐
        { bg: "#A6B5A4", text: "#FFFFFF" }, // 莫蘭迪綠
        { bg: "#D4A5A5", text: "#FFFFFF" }, // 莫蘭迪粉
        { bg: "#B6A6CA", text: "#FFFFFF" }, // 莫蘭迪紫
        { bg: "#8DA399", text: "#FFFFFF" }, // 莫蘭迪青
        { bg: "#C4A484", text: "#FFFFFF" }, // 莫蘭迪卡其
        { bg: "#9E97A8", text: "#FFFFFF" }, // 莫蘭迪灰紫
        { bg: "#8F9E8B", text: "#FFFFFF" }, // 莫蘭迪橄欖綠
        { bg: "#A89F91", text: "#FFFFFF" }, // 莫蘭迪暖灰
        { bg: "#C5B4A1", text: "#FFFFFF" }, // 莫蘭迪米色
        { bg: "#949494", text: "#FFFFFF" }, // 莫蘭迪深灰
    ];

    // 使用 DJB2 hash 演算法，減少碰撞
    let hash = 5381;
    for (let i = 0; i < normalizedType.length; i++) {
        hash = (hash << 5) + hash + normalizedType.charCodeAt(i); /* hash * 33 + c */
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

const getCategoryIcon = (type) => {
    const normalizedType = type?.trim().toLowerCase() || "";
    switch (normalizedType) {
        case "全部":
            return <LayoutGrid size={18} />;
        case "路跑":
            return <Footprints size={18} />;
        case "越野跑":
            return <Mountain size={18} />;
        case "三鐵":
            return <Bike size={18} />;
        case "喵喵選物":
            return <ShoppingBag size={18} />;
        case "其他連結":
            return <LinkIcon size={18} />;
        default:
            return <Tag size={18} />;
    }
};

// 骨架屏組件
const SkeletonCategoryTag = () => (
    <div className="category-tag skeleton-loading" style={{ cursor: "default", minWidth: "100px" }}>
        <div
            style={{
                width: "60px",
                height: "18px",
                backgroundColor: "#e0e0e0",
                borderRadius: "4px",
            }}
        ></div>
    </div>
);

const SkeletonRegistrationItem = () => (
    <div className="registration-item skeleton-loading" style={{ cursor: "default" }}>
        <div className="item-left">
            <span
                className="item-type"
                style={{
                    backgroundColor: "#e0e0e0",
                    width: "60px",
                    height: "24px",
                    display: "inline-block",
                }}
            ></span>
            <span
                className="item-date"
                style={{
                    backgroundColor: "#e0e0e0",
                    width: "100px",
                    height: "20px",
                    display: "inline-block",
                    marginLeft: "10px",
                }}
            ></span>
        </div>

        <div className="item-content" style={{ flex: 1 }}>
            <div
                style={{
                    width: "70%",
                    height: "24px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    marginBottom: "8px",
                }}
            ></div>
            <div
                style={{
                    width: "50%",
                    height: "18px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                }}
            ></div>
        </div>

        <div className="item-action">
            <div
                style={{
                    width: "100px",
                    height: "40px",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "8px",
                }}
            ></div>
        </div>
    </div>
);

function GroupRegistration() {
    const [registrationLinks, setRegistrationLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState("全部");
    const location = useLocation();

    useEffect(() => {
        const loadData = async () => {
            try {
                const data = await fetchGroupRegistrations();
                if (data && data.length > 0) {
                    // 過濾掉已下架的項目
                    const activeLinks = data.filter((item) => !item.isArchived);
                    setRegistrationLinks(activeLinks);
                } else {
                    // Fallback to empty or keep loading false
                    setRegistrationLinks([]);
                }
            } catch (error) {
                console.error("Failed to load registration data", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const category = params.get("category");
        if (category) {
            setSelectedCategory(category);
        }
    }, [location.search]);

    // 取得所有不重複的類別
    const categories = [
        "全部",
        ...new Set(registrationLinks.map((item) => item.type).filter(Boolean)),
    ];

    // 計算每個類別的數量
    const categoryCounts = registrationLinks.reduce((acc, item) => {
        const type = item.type;
        if (type) {
            acc[type] = (acc[type] || 0) + 1;
        }
        return acc;
    }, {});

    // 根據選取的類別篩選資料
    const filteredLinks =
        selectedCategory === "全部"
            ? registrationLinks
            : registrationLinks.filter((item) => item.type === selectedCategory);

    return (
        <section className="content-section registration-section">
            {/* 頁面標題 */}
            <div className="page-header">
                <h2>喵喵團報團購優惠</h2>
                <p className="page-description">
                    集結跑友力量，享受團報優惠！以下是目前開放的團報活動，快來一起參加吧～
                </p>
            </div>

            {/* 類別篩選標籤 */}
            <div className="category-filter">
                {loading && registrationLinks.length === 0 ? (
                    // 初次載入顯示骨架屏
                    <>
                        {Array.from({ length: 5 }).map((_, index) => (
                            <SkeletonCategoryTag key={`skeleton-category-${index}`} />
                        ))}
                    </>
                ) : (
                    categories.map((category) => {
                        const isSelected = selectedCategory === category;
                        const count =
                            category === "全部"
                                ? registrationLinks.length
                                : categoryCounts[category] || 0;
                        let style = {};

                        if (isSelected) {
                            if (category === "全部") {
                                style = {
                                    backgroundColor: "#6571aa",
                                    color: "white",
                                    borderColor: "#6571aa",
                                };
                            } else {
                                const color = getMorandiColor(category);
                                style = {
                                    backgroundColor: color.bg,
                                    color: color.text,
                                    borderColor: color.bg,
                                };
                            }
                        }

                        return (
                            <button
                                key={category}
                                className={`category-tag ${isSelected ? "active" : ""}`}
                                onClick={() => {
                                    setSelectedCategory(category);
                                    trackButtonClick(`團報優惠-篩選類別按鈕-${category}`, category);
                                }}
                                style={style}
                            >
                                {getCategoryIcon(category)}
                                {category} <span className="count">{count}</span>
                            </button>
                        );
                    })
                )}
            </div>

            {/* 團報列表 */}
            <div className="registration-list">
                {loading && registrationLinks.length === 0 ? (
                    // 初次載入顯示骨架屏
                    <>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <SkeletonRegistrationItem key={`skeleton-item-${index}`} />
                        ))}
                    </>
                ) : filteredLinks.length > 0 ? (
                    filteredLinks.map((item) => {
                        const typeStyle = getMorandiColor(item.type);
                        const isShopItem = item.type?.trim() === "喵喵選物";
                        const isMeowItem = item.type?.trim() === "其他連結";

                        return (
                            <div key={item.id} className="registration-item">
                                <div className="item-left">
                                    <span
                                        className="item-type"
                                        style={{
                                            backgroundColor: typeStyle.bg,
                                            color: typeStyle.text,
                                        }}
                                    >
                                        {item.type}
                                    </span>
                                    {!isShopItem && !isMeowItem && item.eventDate && (
                                        <span className="item-date">{item.eventDate}</span>
                                    )}
                                </div>

                                <div className="item-content">
                                    <h3 className="item-title">{item.eventName}</h3>
                                    {item.eventSubtitle && (
                                        <p className="item-subtitle">{item.eventSubtitle}</p>
                                    )}
                                    {item.deadline && (
                                        <div className="item-deadline">
                                            <span className="deadline-icon">⏰</span>
                                            <span className="deadline-text">
                                                收單日期：{item.deadline}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="item-action">
                                    <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`action-btn ${
                                            isShopItem
                                                ? "btn-shop"
                                                : isMeowItem
                                                ? "btn-view"
                                                : "btn-register"
                                        }`}
                                        onClick={() =>
                                            trackButtonClick(
                                                `團報優惠-前往按鈕-${item.eventName}`,
                                                item.eventName
                                            )
                                        }
                                    >
                                        {isShopItem
                                            ? "前往購買"
                                            : isMeowItem
                                            ? "前往查看"
                                            : "前往報名"}
                                        <span className="btn-icon">→</span>
                                    </a>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="no-data-message">
                        {selectedCategory === "全部"
                            ? "目前沒有開放的團報活動 😿"
                            : `此類別目前沒有活動 😿`}
                    </div>
                )}
            </div>

            {/* 注意事項 */}
            <div className="notice-section">
                <h2>注意事項</h2>
                <ul className="notice-list">
                    <li>請注意各活動的報名截止時間，逾期將無法參加團報優惠</li>
                    <li>團報優惠僅限指定連結報名才享有優惠價格</li>
                    <li>如有任何問題，歡迎透過 Instagram 私訊詢問</li>
                    <li>賽事資訊若有異動，以主辦單位公告為準</li>
                </ul>
            </div>
        </section>
    );
}

export default GroupRegistration;
